import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Stack, Typography, Switch, FormControlLabel, Button, Card, CardContent,
  Chip, Collapse, IconButton, Select, MenuItem, TextField, Divider, Alert,
  FormControl, InputLabel, Tooltip, CircularProgress,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { botsApi } from "../../../api/bots.js";
import type { WorkflowConfig, Trigger, CollectField, CollectFieldType, HandoffDestination } from "@chatfab/shared";
import { SUGGEST_LABEL_MAX_CHARS, SUGGEST_TRIGGERS_PER_BOT_MAX } from "@chatfab/shared";

// ── Predefined intent templates ───────────────────────────────────────────

const INTENT_TEMPLATES: Record<string, { label: string; description: string }> = {
  needs_human: {
    label: "Needs human / unresolved issue",
    description: "User says the bot's answer didn't help, asks to speak with a person, requests human support, or expresses frustration that the issue is unresolved",
  },
  billing_question: {
    label: "Billing question",
    description: "User asks about charges, invoices, payment methods, refunds, subscription billing, or pricing of their current plan",
  },
  cancellation_request: {
    label: "Cancellation request",
    description: "User wants to cancel their account, subscription, or service, or asks about the cancellation process",
  },
  bug_report: {
    label: "Bug report",
    description: "User reports something broken, not working, an error message, or unexpected behavior in the product",
  },
  sales_inquiry: {
    label: "Sales inquiry",
    description: "User asks about pricing for new purchases, plan comparisons, enterprise plans, or wants to talk to the sales team",
  },
  feedback_complaint: {
    label: "Feedback / complaint",
    description: "User provides feedback about the product, files a complaint, or expresses dissatisfaction with the service",
  },
  custom: {
    label: "Custom…",
    description: "",
  },
};

const DESTINATION_LABELS: Record<string, string> = {
  webhook:   "Webhook (POST to URL)",
  email:     "Email",
  slack:     "Slack",
  dashboard: "Store in dashboard",
};

const FIELD_TYPE_LABELS: Record<string, string> = {
  text:      "Text",
  email:     "Email",
  number:    "Number",
  phone:     "Phone",
  selection: "Selection",
};

// ── Helpers ───────────────────────────────────────────────────────────────

function makeTrigger(): Trigger {
  return {
    id: `trig_${Date.now()}`,
    intent: "custom",
    intent_template: "custom",
    description: "",
    collect: [],
    handoff: { destination: "dashboard", include: [], message: "" },
  };
}

function makeField(): CollectField {
  return { field: "", type: "text", prompt: "" };
}

// ── TriggerCard ───────────────────────────────────────────────────────────

interface TriggerCardProps {
  trigger: Trigger;
  index: number;
  expanded: boolean;
  allowedDestinations: string[];
  onChange: (updated: Trigger) => void;
  onRemove: () => void;
  onToggleExpand: () => void;
  /** True when enabling suggest_in_chat on this trigger would exceed the bot-wide cap. */
  suggestCapReached: boolean;
}

function defaultSuggestLabel(trigger: Trigger): string {
  const raw = trigger.intent && trigger.intent !== "custom"
    ? trigger.intent.replace(/_/g, " ")
    : "Quick action";
  const titled = raw.replace(/\b\w/g, c => c.toUpperCase());
  return titled.slice(0, SUGGEST_LABEL_MAX_CHARS);
}

function TriggerCard({ trigger, index, expanded, allowedDestinations, onChange, onRemove, onToggleExpand, suggestCapReached }: TriggerCardProps) {
  const templateKey = trigger.intent_template || "custom";
  const fieldCount  = trigger.collect.length;
  const destLabel   = DESTINATION_LABELS[trigger.handoff.destination] ?? trigger.handoff.destination;

  const setField = <K extends keyof Trigger>(key: K, val: Trigger[K]) =>
    onChange({ ...trigger, [key]: val });

  const setHandoff = <K extends keyof Trigger["handoff"]>(key: K, val: Trigger["handoff"][K]) =>
    onChange({ ...trigger, handoff: { ...trigger.handoff, [key]: val } });

  const handleTemplateChange = (template: string) => {
    const tmpl = INTENT_TEMPLATES[template];
    onChange({
      ...trigger,
      intent: template === "custom" ? trigger.intent : template,
      intent_template: template,
      description: tmpl?.description ?? trigger.description,
    });
  };

  const updateCollectField = (i: number, patch: Partial<CollectField>) => {
    const oldField = trigger.collect[i];
    const updated  = trigger.collect.map((f, idx) => idx === i ? { ...f, ...patch } : f);
    // Keep include in sync with field name changes
    if ("field" in patch && patch.field !== oldField.field) {
      const cur = trigger.handoff.include ?? [];
      let next  = cur.filter(k => k !== oldField.field);
      if (patch.field) next = [...next, patch.field];
      onChange({ ...trigger, collect: updated, handoff: { ...trigger.handoff, include: next } });
    } else {
      setField("collect", updated);
    }
  };

  const removeCollectField = (i: number) => {
    setField("collect", trigger.collect.filter((_, idx) => idx !== i));
  };

  const addCollectField = () => setField("collect", [...trigger.collect, makeField()]);

  // Fields listed in handoff.include
  const toggleInclude = (fieldName: string) => {
    const cur = trigger.handoff.include ?? [];
    const next = cur.includes(fieldName) ? cur.filter(k => k !== fieldName) : [...cur, fieldName];
    setHandoff("include", next);
  };

  const includeableFields = [
    ...trigger.collect.filter(f => f.field).map(f => f.field),
    "conversation_summary",
  ];

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      {/* ── Collapsed header ── */}
      <Box
        onClick={onToggleExpand}
        sx={{ px: 2, py: 1.5, cursor: "pointer", "&:hover": { bgcolor: "action.hover" } }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          {/* Number badge */}
          <Box sx={{
            width: 26, height: 26, borderRadius: "50%",
            bgcolor: "primary.main", color: "white",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 12, fontWeight: 700, flexShrink: 0,
          }}>
            {index + 1}
          </Box>

          {/* Title */}
          <Typography variant="body2" fontWeight={500} sx={{ flex: 1, minWidth: 0 }} noWrap>
            {templateKey !== "custom"
              ? (INTENT_TEMPLATES[templateKey]?.label ?? trigger.intent)
              : (trigger.intent || "Custom trigger")}
          </Typography>

          {/* Summary badges */}
          <Stack direction="row" spacing={0.5} flexShrink={0}>
            <Chip label="Detect" size="small" sx={{ bgcolor: "primary.50", color: "primary.700", fontSize: 11, height: 20 }} />
            {fieldCount > 0 && (
              <Chip label={`${fieldCount} field${fieldCount !== 1 ? "s" : ""}`} size="small"
                sx={{ bgcolor: "warning.50", color: "warning.800", fontSize: 11, height: 20 }} />
            )}
            <Chip label={destLabel.split(" ")[0]} size="small"
              sx={{ bgcolor: "success.50", color: "success.800", fontSize: 11, height: 20 }} />
          </Stack>

          {/* Controls */}
          <Tooltip title="Remove trigger">
            <IconButton size="small" onClick={e => { e.stopPropagation(); onRemove(); }}
              sx={{ color: "error.main" }}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <IconButton size="small">{expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>
        </Stack>
      </Box>

      <Collapse in={expanded}>
        <Divider />
        <CardContent sx={{ pt: 2.5 }}>
          <Stack spacing={3}>

            {/* ── Section 1: Detect ── */}
            <Box>
              <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
                <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "primary.main" }} />
                <Typography variant="caption" fontWeight={600} color="primary.main" textTransform="uppercase" letterSpacing={0.5}>
                  Detect
                </Typography>
                <Typography variant="caption" color="text.secondary">When this intent is detected</Typography>
              </Stack>
              <Stack spacing={1.5}>
                <FormControl size="small" sx={{ width: 280 }}>
                  <InputLabel>Intent template</InputLabel>
                  <Select
                    label="Intent template"
                    value={templateKey}
                    onChange={e => handleTemplateChange(e.target.value)}
                  >
                    {Object.entries(INTENT_TEMPLATES).map(([key, tmpl]) => (
                      <MenuItem key={key} value={key}>{tmpl.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {templateKey === "custom" && (
                  <TextField
                    size="small" label="Intent name (no spaces)" sx={{ width: 280 }}
                    value={trigger.intent === "custom" ? "" : trigger.intent}
                    onChange={e => setField("intent", e.target.value.toLowerCase().replace(/\s+/g, "_"))}
                    placeholder="e.g. refund_request"
                  />
                )}
                <TextField
                  size="small" label="Description (used in LLM classification)" fullWidth multiline rows={2}
                  value={trigger.description}
                  onChange={e => setField("description", e.target.value)}
                  placeholder="Describe when this intent should trigger, in plain English"
                />
              </Stack>
            </Box>

            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <ArrowDownwardIcon sx={{ color: "text.disabled", fontSize: 18 }} />
            </Box>

            {/* ── Section 2: Collect ── */}
            <Box>
              <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
                <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "warning.main" }} />
                <Typography variant="caption" fontWeight={600} color="warning.main" textTransform="uppercase" letterSpacing={0.5}>
                  Collect
                </Typography>
                <Typography variant="caption" color="text.secondary">Gather information from user (optional)</Typography>
              </Stack>

              <Stack spacing={1}>
                {trigger.collect.map((field, fi) => (
                  <Stack key={fi} direction="row" spacing={1} alignItems="flex-start">
                    <TextField
                      size="small" label="Field name" sx={{ width: 130 }}
                      value={field.field}
                      onChange={e => updateCollectField(fi, { field: e.target.value.toLowerCase().replace(/\s+/g, "_") })}
                      placeholder="email"
                    />
                    <FormControl size="small" sx={{ width: 120 }}>
                      <InputLabel>Type</InputLabel>
                      <Select
                        label="Type"
                        value={field.type}
                        onChange={e => updateCollectField(fi, { type: e.target.value as CollectFieldType })}
                      >
                        {Object.entries(FIELD_TYPE_LABELS).map(([k, v]) => (
                          <MenuItem key={k} value={k}>{v}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <TextField
                      size="small" label={field.type === "selection" ? "Options (A / B / C)" : "Prompt"}
                      sx={{ flex: 1 }}
                      value={field.prompt}
                      onChange={e => updateCollectField(fi, { prompt: e.target.value })}
                      placeholder={field.type === "selection" ? "Option A / Option B / Option C" : "What should the bot ask?"}
                    />
                    <IconButton size="small" onClick={() => removeCollectField(fi)} sx={{ mt: 0.5, color: "error.main" }}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                ))}
                <Box>
                  <Button size="small" startIcon={<AddIcon />} onClick={addCollectField}
                    sx={{ color: "text.secondary", fontWeight: 400 }}>
                    Add field
                  </Button>
                </Box>
              </Stack>
            </Box>

            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <ArrowDownwardIcon sx={{ color: "text.disabled", fontSize: 18 }} />
            </Box>

            {/* ── Section 3: Handoff ── */}
            <Box>
              <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
                <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "success.main" }} />
                <Typography variant="caption" fontWeight={600} color="success.main" textTransform="uppercase" letterSpacing={0.5}>
                  Handoff
                </Typography>
                <Typography variant="caption" color="text.secondary">Then hand off to</Typography>
              </Stack>
              <Stack spacing={1.5}>
                <FormControl size="small" sx={{ width: 240 }}>
                  <InputLabel>Destination</InputLabel>
                  <Select
                    label="Destination"
                    value={trigger.handoff.destination}
                    onChange={e => setHandoff("destination", e.target.value as HandoffDestination)}
                  >
                    {Object.entries(DESTINATION_LABELS).map(([k, v]) => (
                      <MenuItem key={k} value={k} disabled={!allowedDestinations.includes(k)}>
                        {v}
                        {!allowedDestinations.includes(k) && " (upgrade required)"}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {(trigger.handoff.destination === "webhook" || trigger.handoff.destination === "slack") && (
                  <TextField
                    size="small" label={trigger.handoff.destination === "slack" ? "Slack webhook URL" : "Endpoint URL"}
                    fullWidth
                    value={trigger.handoff.url ?? ""}
                    onChange={e => setHandoff("url", e.target.value)}
                    placeholder="https://"
                  />
                )}

                {trigger.handoff.destination === "email" && (
                  <TextField
                    size="small" label="Send to (email address)"
                    sx={{ width: 280 }}
                    value={trigger.handoff.to ?? ""}
                    onChange={e => setHandoff("to", e.target.value)}
                    placeholder="you@company.com"
                  />
                )}

                {/* Include fields */}
                {includeableFields.length > 0 && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" mb={0.5} display="block">
                      Include in payload:
                    </Typography>
                    <Stack direction="row" flexWrap="wrap" gap={0.75}>
                      {includeableFields.map(name => {
                        const active = (trigger.handoff.include ?? []).includes(name);
                        return (
                          <Chip
                            key={name}
                            label={name}
                            size="small"
                            clickable
                            variant={active ? "filled" : "outlined"}
                            color={active ? "primary" : "default"}
                            onClick={() => toggleInclude(name)}
                          />
                        );
                      })}
                    </Stack>
                  </Box>
                )}

                <TextField
                  size="small" label="Farewell message" fullWidth
                  value={trigger.handoff.message}
                  onChange={e => setHandoff("message", e.target.value)}
                  placeholder="Thanks! A team member will follow up within 2 hours."
                />
              </Stack>
            </Box>

            {/* ── Section 4: Suggest in chat ── */}
            <Divider />
            <Box>
              <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "info.main" }} />
                <Typography variant="caption" fontWeight={600} color="info.main" textTransform="uppercase" letterSpacing={0.5}>
                  Suggest in chat
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Show a clickable chip above the chat input
                </Typography>
              </Stack>

              <FormControlLabel
                control={
                  <Switch
                    checked={!!trigger.suggest_in_chat}
                    disabled={!trigger.suggest_in_chat && suggestCapReached}
                    onChange={e => {
                      const on = e.target.checked;
                      onChange({
                        ...trigger,
                        suggest_in_chat: on,
                        // Seed a sensible default label when enabling.
                        suggest_label: on && !(trigger.suggest_label ?? "").trim()
                          ? defaultSuggestLabel(trigger)
                          : trigger.suggest_label,
                      });
                    }}
                    color="primary"
                    size="small"
                  />
                }
                label={
                  trigger.suggest_in_chat
                    ? "Shown as a chip"
                    : suggestCapReached
                      ? `At most ${SUGGEST_TRIGGERS_PER_BOT_MAX} chips can be shown at once`
                      : "Not suggested — users must ask naturally"
                }
              />

              {trigger.suggest_in_chat && (
                <Box sx={{ mt: 1.5 }}>
                  <TextField
                    size="small"
                    label="Chip label"
                    sx={{ width: 320 }}
                    value={trigger.suggest_label ?? ""}
                    onChange={e => setField("suggest_label", e.target.value.slice(0, SUGGEST_LABEL_MAX_CHARS))}
                    placeholder="Contact a tax pro"
                    helperText={`${(trigger.suggest_label ?? "").length}/${SUGGEST_LABEL_MAX_CHARS} — short imperative works best`}
                    inputProps={{ maxLength: SUGGEST_LABEL_MAX_CHARS }}
                  />
                </Box>
              )}
            </Box>

          </Stack>
        </CardContent>
      </Collapse>
    </Card>
  );
}

// ── WorkflowTab ───────────────────────────────────────────────────────────

export function WorkflowTab({ botId }: { botId: string }) {
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState<string | null>(null);
  const [saved,   setSaved]   = useState(false);

  const [savedWorkflow,     setSavedWorkflow]     = useState<WorkflowConfig | null>(null);
  const [localWorkflow,     setLocalWorkflow]     = useState<WorkflowConfig>({ enabled: true, triggers: [] });
  const [tier,              setTier]              = useState("free");
  const [triggerLimit,      setTriggerLimit]      = useState(0);
  const [allowedDests,      setAllowedDests]      = useState<string[]>([]);
  const [expandedIds,       setExpandedIds]       = useState<Set<string>>(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await botsApi.getWorkflow(botId);
      setTier(data.tier);
      setTriggerLimit(data.tier_trigger_limit);
      setAllowedDests(data.allowed_destinations);
      const wf = data.workflow ?? { enabled: true, triggers: [] };
      setSavedWorkflow(wf);
      setLocalWorkflow(structuredClone(wf));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load workflow");
    } finally {
      setLoading(false);
    }
  }, [botId]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const result = await botsApi.saveWorkflow(botId, localWorkflow);
      setSavedWorkflow(result);
      setLocalWorkflow(structuredClone(result));
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const discard = () => {
    if (savedWorkflow) setLocalWorkflow(structuredClone(savedWorkflow));
    setError(null);
  };

  const addTrigger = () => {
    if (localWorkflow.triggers.length >= triggerLimit) return;
    const t = makeTrigger();
    setLocalWorkflow(w => ({ ...w, triggers: [...w.triggers, t] }));
    setExpandedIds(s => new Set([...s, t.id]));
  };

  const removeTrigger = (id: string) => {
    setLocalWorkflow(w => ({ ...w, triggers: w.triggers.filter(t => t.id !== id) }));
    setExpandedIds(s => { const n = new Set(s); n.delete(id); return n; });
  };

  const updateTrigger = (id: string, updated: Trigger) => {
    setLocalWorkflow(w => ({ ...w, triggers: w.triggers.map(t => t.id === id ? updated : t) }));
  };

  const toggleExpand = (id: string) => {
    setExpandedIds(s => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  // Upgrade prompt for free tier
  if (tier === "free") {
    return (
      <Box sx={{ py: 6, textAlign: "center", maxWidth: 480, mx: "auto" }}>
        <Typography variant="h5" mb={1}>Trigger workflows</Typography>
        <Typography variant="body2" color="text.secondary" mb={3}>
          Define intent-driven flows that collect information from users and hand off to your team.
          Available on Starter and above.
        </Typography>
        <Button variant="contained">Upgrade plan</Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* ── Header ── */}
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" mb={3}>
        <Box>
          <Typography variant="h4">Trigger workflows</Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            When the bot detects an intent, collect info and take action.
          </Typography>
        </Box>
        <FormControlLabel
          control={
            <Switch
              checked={localWorkflow.enabled}
              onChange={e => setLocalWorkflow(w => ({ ...w, enabled: e.target.checked }))}
              color="primary"
            />
          }
          label={localWorkflow.enabled ? "Enabled" : "Disabled"}
          labelPlacement="start"
          sx={{ mr: 0 }}
        />
      </Stack>

      {/* ── Trigger list ── */}
      <Box sx={{ opacity: localWorkflow.enabled ? 1 : 0.5, transition: "opacity 0.2s" }}>
        {(() => {
          const suggestedCount = localWorkflow.triggers.filter(t => t.suggest_in_chat).length;
          return localWorkflow.triggers.map((trigger, i) => (
            <TriggerCard
              key={trigger.id}
              trigger={trigger}
              index={i}
              expanded={expandedIds.has(trigger.id)}
              allowedDestinations={allowedDests}
              onChange={updated => updateTrigger(trigger.id, updated)}
              onRemove={() => removeTrigger(trigger.id)}
              onToggleExpand={() => toggleExpand(trigger.id)}
              suggestCapReached={suggestedCount >= SUGGEST_TRIGGERS_PER_BOT_MAX}
            />
          ));
        })()}

        {/* Add trigger button */}
        {localWorkflow.triggers.length < triggerLimit ? (
          <Button
            fullWidth
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={addTrigger}
            sx={{ borderStyle: "dashed", color: "text.secondary", borderColor: "divider", py: 1.5 }}
          >
            Add trigger
          </Button>
        ) : (
          <Alert severity="info" sx={{ mb: 1 }}>
            You've reached the trigger limit for your plan ({triggerLimit}). Upgrade to add more.
          </Alert>
        )}

        {localWorkflow.triggers.length === 0 && (
          <Box sx={{ textAlign: "center", py: 3, color: "text.secondary" }}>
            <Typography variant="body2">
              No triggers yet. Add one to start guiding your bot's conversations.
            </Typography>
          </Box>
        )}
      </Box>

      {/* ── Save bar ── */}
      <Box sx={{ mt: 3, pt: 2.5, borderTop: 1, borderColor: "divider" }}>
        <Stack direction="row" spacing={1.5} justifyContent="flex-end" alignItems="center">
          {saved  && <Alert severity="success" sx={{ py: 0.5 }}>Saved!</Alert>}
          {error  && <Alert severity="error"   sx={{ py: 0.5 }}>{error}</Alert>}
          <Button variant="text" color="inherit" onClick={discard} disabled={saving}>
            Discard changes
          </Button>
          <Button variant="contained" onClick={save} disabled={saving}>
            {saving ? "Saving…" : "Save workflow"}
          </Button>
        </Stack>
      </Box>
    </Box>
  );
}
